import { useTranslation } from 'react-i18next';
import theme from 'shared/styles/theme';

interface FundingBoundary {
  amount?: string | number;
  formatted?: string;
}

interface UseTransferLimitationsParams {
  transferAmount: string;
  maxAmount?: FundingBoundary | null;
  minAmount?: FundingBoundary | null;
  limitErrorMessage?: string | null;
}

interface TransferLimitationsResult {
  transferAmountNumber: number;
  isSubmitDisabled: boolean;
  transferLimitations: string;
  transferLimitationsIcon: string;
  transferLimitationsTextColor: string;
  maxFundError: boolean;
  minFundError: boolean;
}

const useTransferLimitations = ({
  transferAmount,
  maxAmount,
  minAmount,
  limitErrorMessage,
}: UseTransferLimitationsParams): TransferLimitationsResult => {
  const { t } = useTranslation('moveMoney', { keyPrefix: 'transferStrings' });

  const transferAmountNumber = Number(transferAmount);
  const hasBounds = Boolean(maxAmount && minAmount);
  const isSubmitDisabled = hasBounds
    ? transferAmountNumber > Number(maxAmount?.amount) ||
      transferAmountNumber < Number(minAmount?.amount) ||
      Number.isNaN(transferAmountNumber)
    : Number.isNaN(transferAmountNumber);

  let transferLimitations = '';
  let transferLimitationsIcon = '';
  let transferLimitationsTextColor = theme.color.text.secondary;

  const maxFundError =
    Number(maxAmount?.amount) < transferAmountNumber &&
    transferAmountNumber > 0;
  const minFundError =
    Number(minAmount?.amount) > transferAmountNumber &&
    transferAmountNumber > 0;

  if (limitErrorMessage) {
    transferLimitations = limitErrorMessage;
    transferLimitationsTextColor = theme.color.status.negative;
    transferLimitationsIcon = 'STATUS_ERROR';
  } else if (minFundError) {
    transferLimitations = t('minLimitError', {
      minAmount: minAmount?.formatted,
    });
    transferLimitationsTextColor = theme.color.status.negative;
    transferLimitationsIcon = 'STATUS_ERROR';
  } else if (maxFundError) {
    transferLimitations = t('maxLimitError', {
      maxAmount: maxAmount?.formatted,
    });
    transferLimitationsTextColor = theme.color.status.negative;
    transferLimitationsIcon = 'STATUS_ERROR';
  } else if (transferAmountNumber > 0) {
    transferLimitations = t('instantNoFee');
    transferLimitationsTextColor = theme.color.status.positive;
    transferLimitationsIcon = 'BOLT';
  } else if (transferAmountNumber === 0) {
    transferLimitations = t('amountRequired');
    transferLimitationsTextColor = theme.color.status.negative;
    transferLimitationsIcon = 'STATUS_ERROR';
  }

  return {
    transferAmountNumber,
    isSubmitDisabled,
    transferLimitations,
    transferLimitationsIcon,
    transferLimitationsTextColor,
    maxFundError,
    minFundError,
  };
};

export default useTransferLimitations;
